import { Trans, useTranslation } from 'next-i18next';
import { ChangeEvent, FC, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, ButtonV2, InputV2 } from '@bridebook/ui';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import { zodResolver } from '@hookform/resolvers/zod';
import { IPinFormFields, Palettes } from '@bridebook/web/lib/wedding-website/types';
import { IThemedComponentStyles } from '@bridebook/web/lib/wedding-website/utils/get-themed-component-styles';
import { getPinFormValidationSchema } from '@bridebook/web/lib/wedding-website/utils/validation';
import styles from './password-protected-entry.style';

interface IProps {
  onPasswordEntered: (pin: string) => void;
  firstName: string;
  secondName: string;
  loading?: boolean;
  themeStyles: IThemedComponentStyles;
  palette: Palettes;
  invalidPinError?: boolean; // Renamed from hasError to be more descriptive
}

export const PasswordProtectedEntry: FC<IProps> = ({
  onPasswordEntered,
  firstName,
  secondName,
  loading,
  themeStyles,
  palette,
  invalidPinError,
}) => {
  const { t } = useTranslation('weddingWebsite');
  const missingPartnerName = t('partners.missingNo');
  const displayFirstName = firstName || missingPartnerName;
  const displaySecondName = secondName || missingPartnerName;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    clearErrors,
  } = useForm<IPinFormFields>({
    resolver: zodResolver(getPinFormValidationSchema(t)),
    defaultValues: {
      pin: '',
    },
  });

  useEffect(() => {
    if (invalidPinError && !loading) {
      setError('pin', {
        type: 'manual',
        message: t('validation.pin.invalid'),
      });
    }
  }, [invalidPinError, setError, loading, t]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (event: string | ChangeEvent<Element>) => void,
    ) => {
      if (invalidPinError) {
        clearErrors('pin');
      }
      onChange(e);
    },
    [invalidPinError, clearErrors],
  );

  const onSubmit = (data: IPinFormFields) => {
    onPasswordEntered(data.pin);
  };

  return (
    <Box style={styles.wrapper(palette)}>
      <Box style={styles.fadingOverlay} />
      <Box style={styles.contentWrapper}>
        <Box style={styles.paletteRosesAsset('top-left', palette)} />
        <Box style={styles.paletteRosesAsset('bottom-right', palette)} />
        <Box style={styles.container}>
          <Trans
            ns="weddingWebsite"
            i18nKey="previewMode.password.headings"
            values={{ firstPartnerName: displayFirstName, secondPartnerName: displaySecondName }}>
            <Box style={themeStyles.h3}>Welcome to</Box>
            <Box style={mergeStyles([themeStyles.h2, styles.heading])}>
              <Box style={styles.paletteFoliageAsset('left', palette)} />
              <Box style={styles.paletteWhimsicalAsset('left', palette)} />
              {'{{ firstPartnerName }} & {{ secondPartnerName, possessive }} wedding!'}
              <Box style={styles.paletteFoliageAsset('right', palette)} />
              <Box style={styles.paletteWhimsicalAsset('right', palette)} />
            </Box>
          </Trans>
        </Box>
        <Box style={styles.passwordWrapper}>
          <Box style={themeStyles.p}>{t('previewMode.password.tip')}</Box>

          <form onSubmit={handleSubmit(onSubmit)} name="password-entry-form">
            <Controller
              control={control}
              name="pin"
              render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
                <InputV2
                  value={value}
                  onChange={(e) => handleChange(e, onChange)}
                  onBlur={onBlur}
                  name={name}
                  label={<Box style={themeStyles.p}>{t('previewMode.password.label')}</Box>}
                  placeholder={t('previewMode.password.placeholder')}
                  containerStyle={styles.passwordContainer}
                  inputStyle={themeStyles.p}
                  disabled={loading || isSubmitting}
                  error={error?.message}
                />
              )}
            />

            <ButtonV2
              type="submit"
              w={313}
              loading={loading || isSubmitting}
              disabled={loading || isSubmitting}
              styleWrapper={mergeStyles([themeStyles.button, styles.button])}>
              <Box style={themeStyles.buttonText}>{t('previewMode.password.cta')}</Box>
            </ButtonV2>
          </form>
        </Box>
      </Box>
      <Box style={styles.fadingOverlay} />
    </Box>
  );
};
